import { useRouterService } from '@hooks/common/UseRouterService';
import { useMarketConquest } from '@hooks/useMarketConquest';
import { theme } from '@theme';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { NavType } from '../../../../enums/NavType';
import { useOutsideClickDetection, useUserDealer } from '../../../../hooks';
import { useCommon } from '../../../../hooks/common/UseCommon';
import { useSearchDeals } from '../../../../hooks/header/UseSearchDeals';
import { useMarketImpact } from '../../../../hooks/side-nav/UseMarketImpact';
import { useSideNav } from '../../../../hooks/side-nav/UseSideNav';
import { AppState } from '../../../../store/AppState';
import { CurrentUser } from '../../../../types';
import { DataExportCount } from '../../../../types/DataExport';
import { DealerState } from '../../../../types/DealerState';
import { MarketImpactAvailableService } from '../../../../types/MarketImpact';
import { Icon } from '../../../style';

export type DesktopSideNavProps = {
  currentUrl: string;
};

const DesktopSideNav: FC<DesktopSideNavProps> = ({ currentUrl }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const privateIncentiveCount = useSelector<AppState, DataExportCount>(
    (state) => state.dealer.privateIncentiveCount,
  );
  const { dealerInfos, featureToggle, storeServices, turnStatus } = useSelector<
    AppState,
    DealerState
  >((state) => state.dealer);
  const currentUser = useSelector<AppState, CurrentUser>((state) => state.currentUser);

  const { isActive, redirectToUrl, isActiveTab } = useSideNav(currentUrl);
  const { getMarketImpactServicesMode, getAvailableMarketImpactServices } = useMarketImpact();
  const { isPrimaryUser, dealerList } = useCommon();
  const { isIncentiveAdvantageType, isEssentialType } = useUserDealer();
  const { isDealerOptIn } = useSearchDeals();
  const { goToMarketImpact } = useRouterService();

  const hasServicePortfolioEnabled = storeServices && storeServices.hasServicePortfolio;
  const isServicePortfolio = featureToggle.servicePortfolio;
  const servicePortfolioNav =
    isServicePortfolio && hasServicePortfolioEnabled ? NavType.Service : NavType.ServiceToSales;
  const permissions = currentUser.permissions;
  const acquirePermissions = useMarketConquest().permissions;
  const hasTurnEnabled = turnStatus;
  const hasDigitalAudienceEnabled =
    storeServices &&
    storeServices.hasDigitalAudience &&
    permissions.indexOf('manageDigitalAudience') > -1;
  const isPrivateIncentiveLookup = dealerInfos.some((e) => dealerList.includes(e.brandName));
  const marketImpactAvailableServices = getAvailableMarketImpactServices(
    getMarketImpactServicesMode(storeServices),
    isPrimaryUser(currentUser),
    permissions,
  );
  const hasMarketImpactSummaryReportFeature = featureToggle.marketImpactSummaryReport;
  const hasMarketImpactSummaryReportService =
    storeServices &&
    !storeServices?.hasMarketImpactNewVehicles &&
    !storeServices.hasMarketImpactPreowned;
  const isOpetIn = dealerInfos.map((e) => isDealerOptIn(e.dealerId));
  const isNonMBorBMWorMINIDealer = dealerInfos.every(
    (e) => e.brandName !== 'BMW' && e.brandName !== 'Mercedes-Benz' && e.brandName !== 'MINI',
  );

  const useSimplifiedMarketImpactMenu = marketImpactAvailableServices.length === 1;
  const marketImpactButtonLabel = useSimplifiedMarketImpactMenu
    ? marketImpactAvailableServices[0].type === 'new'
      ? NavType.MarketImpactNew
      : NavType.MarketImpactUsed
    : NavType.MarketImpact;

  const [isMarketImpactDropdownOpen, setIsMarketImpactDropdownOpen] = useState<boolean>(false);
  const [isSideNavOpen, setIsSideNavOpen] = useState<boolean>(false);
  const [hasTouch, setHasTouch] = useState<boolean>(false);

  const hideSideNav = (): void => {
    setIsSideNavOpen(false);
  };

  useOutsideClickDetection(wrapperRef, hideSideNav);

  useEffect(() => {
    if ('ontouchstart' in document) {
      document.body.classList.add('has-touch');
      setHasTouch(true);
    }

    return () => {
      document.body.classList.remove('has-touch');
    };
  }, []);

  const handleSideNavOpen = (): void => {
    const isMarketImpactServiceActive = marketImpactAvailableServices.some(({ type }) =>
      isActive(`market-impact/${type}`),
    );
    if (!useSimplifiedMarketImpactMenu && isMarketImpactServiceActive) {
      setIsMarketImpactDropdownOpen(true);
    }
  };

  const handleSideNavClose = (): void => {
    if (isMarketImpactDropdownOpen) {
      setIsMarketImpactDropdownOpen(false);
    }
  };

  const handleMarketImpactDropdownToggle = (): void => {
    setIsMarketImpactDropdownOpen(!isMarketImpactDropdownOpen);
  };

  const handleMarketImpactButtonClick = (): void => {
    if (useSimplifiedMarketImpactMenu) {
      goToMarketImpact(marketImpactAvailableServices[0].type);
    } else {
      handleMarketImpactDropdownToggle();
    }
  };

  const isMarketImpactMenuAvailable = (): boolean => {
    return !isIncentiveAdvantageType && marketImpactAvailableServices.length > 0;
  };

  const onNavClick = (link: string, e: any = null): void => {
    const pointerType = e.nativeEvent.pointerType;
    const pointerId = e.nativeEvent.pointerId;
    const isMouseClick = pointerType === 'mouse' && pointerId === 1;
    if (isMouseClick !== true) {
      e.preventDefault();
      if (isSideNavOpen) {
        setIsSideNavOpen(false);
        redirectToUrl(link);
      } else {
        setIsSideNavOpen(true);
      }
      return;
    }

    redirectToUrl(link);
  };

  const getLinkClassName = (link: string): string => {
    const isLinkActive = isActiveTab(link);

    if (!link.includes(NavType.MarketImpact)) {
      return 'main-menu__item ' + (isLinkActive ? 'main-menu__item--active' : '');
    } else {
      return `main-menu__item dropdown ${isLinkActive ? 'main-menu__item--active' : ''}`;
    }
  };

  const isMarketImpactSummaryReportEnabled = () =>
    hasMarketImpactSummaryReportFeature &&
    hasMarketImpactSummaryReportService &&
    isNonMBorBMWorMINIDealer &&
    permissions.includes('viewReports') &&
    !isPrimaryUser(currentUser) &&
    !isIncentiveAdvantageType;

  const hasPrivateIncentive = (): boolean => {
    if (privateIncentiveCount && privateIncentiveCount.count.length > 0) {
      return true;
    }
    return false;
  };

  return (
    <div
      ref={wrapperRef}
      className={`main-menu d-print-none ${hasTouch ? 'has-touch' : ''} ${hasTouch && isSideNavOpen ? 'open-tablet' : ''}`}
      onMouseLeave={handleSideNavClose}
      onMouseEnter={handleSideNavOpen}
      data-testid='desktop-side-nav'
    >
      <ul className='main-menu__menu'>
        {isIncentiveAdvantageType && (
          <li className={getLinkClassName(NavType.Portfolio)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.Portfolio, event)}
              data-testid={NavType.Portfolio}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='portfolio' />
              </IconWrapper>
              <span>{NavType.Portfolio}</span>
            </NavLink>
          </li>
        )}
        {!isIncentiveAdvantageType && (
          <li className={getLinkClassName(NavType.Loyalty)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.Loyalty, event)}
              data-testid={NavType.Loyalty}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='portfolio' />
              </IconWrapper>
              <span>{NavType.Loyalty}</span>
            </NavLink>
          </li>
        )}
        {permissions.includes('updateConquest') &&
          !isIncentiveAdvantageType &&
          !isEssentialType &&
          !(isServicePortfolio && !hasServicePortfolioEnabled) && (
            <li className={getLinkClassName(servicePortfolioNav)}>
              <NavLink
                onClick={(event) => onNavClick(servicePortfolioNav, event)}
                data-testid={servicePortfolioNav}
              >
                <IconWrapper>
                  <Icon
                    size={theme.typography.h4}
                    type={
                      isServicePortfolio && hasServicePortfolioEnabled
                        ? 'service-conquest'
                        : 'conquest'
                    }
                  />
                </IconWrapper>
                <span>{servicePortfolioNav}</span>
              </NavLink>
            </li>
          )}
        {acquirePermissions.displayAcquireInMainMenu && (
          <li className={getLinkClassName(NavType.Acquire)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.Acquire, event)}
              data-testid={NavType.Acquire}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='market-conquest1' />
              </IconWrapper>
              <span>Acquire</span>
            </NavLink>
          </li>
        )}
        {hasTurnEnabled && (
          <li className={getLinkClassName(NavType.Turn)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.Turn, event)}
              data-testid={NavType.Turn}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='turn' />
              </IconWrapper>
              <span>{NavType.Turn}</span>
            </NavLink>
          </li>
        )}
        {hasDigitalAudienceEnabled && (
          <li className={getLinkClassName(NavType.DigitalAudience)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.DigitalAudience, event)}
              data-testid={NavType.DigitalAudience}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='audience' />
              </IconWrapper>
              <span>{NavType.DigitalAudience}</span>
            </NavLink>
          </li>
        )}
        {(permissions.includes('viewReports') || isIncentiveAdvantageType) && (
          <li className={getLinkClassName(NavType.Reporting)}>
            <NavLink
              onClick={(event) =>
                onNavClick(
                  isIncentiveAdvantageType ? NavType.ReportingPrivateIncentive : NavType.Reporting,
                  event,
                )
              }
              data-testid={NavType.Reporting}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='reporting' />
              </IconWrapper>
              <span>
                {isIncentiveAdvantageType ? NavType.ReportingPrivateIncentive : NavType.Reporting}
              </span>
            </NavLink>
          </li>
        )}
        {(isOpetIn.includes(true) || hasPrivateIncentive()) && isPrivateIncentiveLookup && (
          <li className={getLinkClassName(NavType.PrivateIncentiveLookup)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.PrivateIncentiveLookup, event)}
              data-testid={NavType.PrivateIncentiveLookup}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='private-incentive' />
              </IconWrapper>
              <span>{NavType.PrivateIncentiveLookup}</span>
            </NavLink>
          </li>
        )}
        {isMarketImpactMenuAvailable() && (
          <li className={getLinkClassName(NavType.MarketImpact)}>
            <button className='dropdown__button' onClick={handleMarketImpactButtonClick}>
              <IconWrapper>
                <Icon type='market-impact' size={theme.typography.h4} />
              </IconWrapper>
              <div className='dropdown-button__text'>
                {marketImpactButtonLabel}
                {!useSimplifiedMarketImpactMenu && (
                  <Icon type='angle-down' size={theme.typography.h4} />
                )}
              </div>
            </button>
            {!useSimplifiedMarketImpactMenu && (
              <ul
                className={`dropdown__content ${isMarketImpactDropdownOpen ? 'dropdown__content--open' : ''}`}
              >
                {marketImpactAvailableServices.map((service: MarketImpactAvailableService) => {
                  return (
                    <li className='dropdown__item' key={service.type}>
                      <a
                        className={`dropdown__link ${isActive('market-impact/' + service.type) ? 'dropdown__link--active' : ''}`}
                        onClick={(event) =>
                          onNavClick(
                            service.type == 'new'
                              ? NavType.MarketImpactNew
                              : NavType.MarketImpactUsed,
                            event,
                          )
                        }
                      >
                        {service.type}
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        )}
        {isMarketImpactSummaryReportEnabled() && (
          <li className={getLinkClassName(NavType.MarketImpactSummary)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.MarketImpactSummary, event)}
              data-testid={NavType.MarketImpactSummary}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='market-impact' />
              </IconWrapper>
              <span>{NavType.MarketImpactSummary}</span>
            </NavLink>
          </li>
        )}
        {(permissions.includes('updateDealerSettings') ||
          isIncentiveAdvantageType ||
          isEssentialType) && (
          <li className={getLinkClassName(NavType.StoreSettings)}>
            <NavLink
              onClick={(event) => onNavClick(NavType.StoreSettings, event)}
              data-testid={NavType.StoreSettings}
            >
              <IconWrapper>
                <Icon size={theme.typography.h4} type='gear' />
              </IconWrapper>
              <span>{NavType.StoreSettings}</span>
            </NavLink>
          </li>
        )}
      </ul>
    </div>
  );
};

export default DesktopSideNav;

const IconWrapper = styled.div.attrs({
  className: 'main-menu__icon-wrapper',
})``;

const NavLink = styled.a.attrs({
  className: 'main-menu__link',
})``;
